import { differenceInMilliseconds, format, intervalToDuration, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DEFAULT_DURATION_UNIT_LABELS, DURATION_UNIT_LABELS, DURATION_UNIT_ORDER, M3_DATE_FORMAT, M3_DATE_FORMAT_STRING, type DurationUnit } from '../constants/common';
import { defaultDateFormat, defaultTimeFormat } from 'vince-core/constants/localization';

export const formatDateTimePassed = (dateTime1: string | undefined | null, dateTime2: string | undefined | null, locale: string | undefined): string => {
    if (!dateTime1) {
        return '';
    }
    const dateTimeTo = dateTime2 || new Date();
    const duration = intervalToDuration({ start: dateTime1, end: dateTimeTo });
    const unitLabels = DURATION_UNIT_LABELS[(locale || '').trim().toLowerCase()] ?? DEFAULT_DURATION_UNIT_LABELS;

    const largestUnitIndex = DURATION_UNIT_ORDER.findIndex(unit => duration[unit]);
    const unitsToShow: DurationUnit[] = largestUnitIndex === -1
        ? ['seconds']
        : DURATION_UNIT_ORDER.slice(largestUnitIndex, largestUnitIndex + 2);

    const result = unitsToShow
        .filter(unit => duration[unit])
        .map(unit => `${duration[unit]}${unitLabels[unit]}`)
        .join(' ');

    return result || `${Math.abs(differenceInMilliseconds(dateTime1, dateTimeTo))} ms`;
};

const checkForM3Date = (dateString: string): string => {
    let date = dateString;
    if (typeof date !== 'string') {
        date = dateString.toString();
    }

    if (M3_DATE_FORMAT.test(date)) {
        return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
    }
    return date;
};

export const formatDateTime = (dateTime: string | undefined | null, requiredFormat: string | undefined, timezone?: string): string => {
    if (!dateTime) {
        return '';
    }
    try {
        const normalizedDate = checkForM3Date(dateTime);

        if (timezone) {
            const utcDate = new Date(normalizedDate + 'Z');
            return formatInTimeZone(utcDate, timezone, requiredFormat || `${defaultDateFormat} ${defaultTimeFormat}`);
        }
        const dateObject = new Date(normalizedDate);
        return format(dateObject, requiredFormat || `${defaultDateFormat} ${defaultTimeFormat}`);
    } catch (e) { 
        return '';
    }
};

/**
 * Ensures a value is a valid Date object, converting from string if necessary
 * @param date - Date, ISO string, or null
 * @returns Date object or null
 */
export const ensureDateObject = (date: Date | string | null | undefined): Date | null => {
    if (!date) {
        return null;
    }
    if (date instanceof Date) {
        return isValid(date) ? date : null;
    }
    const parsed = typeof date === 'string' ? parseISO(date) : null;
    return parsed && isValid(parsed) ? parsed : null;
};

/**
 * Converts a Date to YYYY-MM-DD format string for date inputs
 * @param date - Date object or null
 * @returns Formatted date string or undefined
 */
export const toDateInputString = (date: Date | null | undefined): string | undefined => {
    if (!date || !(date instanceof Date) || !isValid(date)) {
        return undefined;
    }
    return format(date, 'yyyy-MM-dd');
};

/**
 * Converts a Date to HH:mm format string for time inputs
 * @param date - Date object or null
 * @returns Formatted time string or undefined
 */
export const toTimeInputString = (date: Date | null | undefined): string | undefined => {
    if (!date || !(date instanceof Date) || !isValid(date)) {
        return undefined;
    }
    return format(date, 'HH:mm');
};

/**
 * Parses a date string and returns a Date object or null
 * @param value - Date string or null
 * @returns Date object or null
 */
export const parseDateString = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null;
    }
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
};

/**
 * Converts a date string (ISO, YYYYMMDD, etc.) to M3 compact date format YYYYMMDD.
 */
export const formatToM3Date = (dateTime: string | undefined | null): string => {
    return formatDateTime(dateTime, M3_DATE_FORMAT_STRING);
};
